import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import {
  RISK_LEVELS_LABELS,
  RISK_LEVELS_VALUES,
  SET_SINGLE_CAMPAIGN_ID,
} from 'containers/SpearPhishingAgent/constants'
import {
  selectCampaignsData,
  selectExcludedParticipants,
  selectMetricBannerData,
} from 'containers/SpearPhishingAgent/selectors'
import { getActiveSelectedCampaignIds } from 'containers/SpearPhishingAgent/utils'

import { SerialClicker, Untrained } from 'components/lib/DesignSystem/Icons'
import Badge, { SHAPES, SIZES as BADGE_SIZES } from 'components/lib/DesignSystem/V3/Badge'
import Button, { SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'
import { PSIMA_PENDO_FEATURES } from 'components/pages/SpearPhishingAgent/psimaPendo'

import AgentVerifiedMark from '../CardSection/components/AgentVerifiedMark'

import HexAlertIcon from './icons/HexAlertIcon'
import ShieldOutlineIcon from './icons/ShieldOutlineIcon'

const NUMBER_ANIMATION_DURATION_MS = 350

const RISK_LEVEL_ORDER = [
  RISK_LEVELS_VALUES.HIGH,
  RISK_LEVELS_VALUES.MEDIUM,
  RISK_LEVELS_VALUES.LOW,
]

/*
 * Exposure severity chips.
 *
 * High and Medium use the PSIMA badge token pairs, which are authored as badge
 * background + badge TEXT. Deliberately NOT --risk-high-color / --risk-medium-color:
 * those are gauge/meter fills and only reach 3.3:1 and 1.8:1 respectively against
 * their matching -bg as 12px text, well under the 4.5:1 AA floor.
 *
 * Low uses the --risk-low-* pair, which IS legible as text (5.1:1) and is green —
 * matching the risk gauges and the OSINT exposure legend on the banner above.
 *
 * High deliberately shares its tokens with the serial-clicker chip below, so the
 * two reds in the bar are the same red rather than two near-miss shades.
 */
const RISK_CHIP = {
  [RISK_LEVELS_VALUES.HIGH]: {
    bg: 'var(--psima-serial-clicker-badge-bg)',
    text: 'var(--psima-serial-clicker-badge-text)',
  },
  [RISK_LEVELS_VALUES.MEDIUM]: {
    bg: 'var(--psima-untrained-badge-bg)',
    text: 'var(--psima-untrained-badge-text)',
  },
  [RISK_LEVELS_VALUES.LOW]: {
    bg: 'var(--risk-low-bg)',
    text: 'var(--risk-low-color)',
  },
}

/*
 * Behavioural signal chips.
 *
 * Mirrors RISK_SIGNAL_COLORS and getRiskSignals in CardSection: a serial-clicker
 * chip here carries the same colour AND the same icon as the 22px signal chip
 * shown beside that employee's name in the participants table, so the summary in
 * the bar and the per-employee rows read as one vocabulary.
 */
const BEHAVIORAL_CHIP = {
  serialClicker: {
    bg: 'var(--psima-serial-clicker-badge-bg)',
    text: 'var(--psima-serial-clicker-badge-text)',
    Icon: SerialClicker,
  },
  untrained: {
    bg: 'var(--psima-untrained-badge-bg)',
    text: 'var(--psima-untrained-badge-text)',
    Icon: Untrained,
  },
}

const easeOutCubic = (t) => 1 - (1 - t) ** 3

const useAnimatedNumber = (targetValue, duration = NUMBER_ANIMATION_DURATION_MS) => {
  const [animatedValue, setAnimatedValue] = useState(targetValue)
  const currentValueRef = useRef(targetValue)

  useEffect(() => {
    const startValue = currentValueRef.current
    const delta = targetValue - startValue

    if (delta === 0) {
      return undefined
    }

    let startTimestamp = null
    let frameId = null

    const tick = (timestamp) => {
      if (startTimestamp === null) startTimestamp = timestamp
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)
      const nextValue = startValue + delta * easeOutCubic(progress)
      currentValueRef.current = nextValue
      setAnimatedValue(nextValue)
      if (progress < 1) {
        frameId = requestAnimationFrame(tick)
      }
    }

    frameId = requestAnimationFrame(tick)

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId)
    }
  }, [targetValue, duration])

  return animatedValue
}

const AnimatedNumber = ({ value }) => {
  const animatedValue = useAnimatedNumber(value)
  return <>{Math.round(animatedValue)}</>
}

/*
 * "<count> of <total>", degrading to just "<count>" when the total is missing.
 *
 * Used by Exposure ("Addresses {X} of {Y} external risks") and Behavioral.
 * The totals come from the metrics-banner call, which is a separate request from
 * the cards themselves — so while it is in flight, or if it fails, the total is
 * 0. Rendering "Addresses 3 of 0 external risks" in that window would read as a
 * hard (and wrong) statement, where "Addresses 3 external risks" is simply less
 * complete.
 */
const CountOfTotal = ({ count, total }) => (
  <>
    <AnimatedNumber value={count} />
    {total > 0 && <MetricTotal> of {total}</MetricTotal>}
  </>
)

const Spacer = styled.div`
  height: 130px;
`

const StickyContainer = styled.div`
  position: fixed;
  /* Start at the platform-nav rail's edge so the bar never covers the rail's
     bottom-pinned Settings item; --nav-width is unset under the old AppLayout.
     Stay below the nav z-index so an open mobile drawer / Settings pin wins. */
  left: var(--nav-width, 0);
  right: 0;
  bottom: 0;
  z-index: calc(var(--z-index-navigation, 100) - 1);
  background: var(--psima-impact-bar-bg);
  border-top: 1px solid var(--card-border-color);
  box-shadow: var(--psima-impact-bar-shadow);
  padding: 8px 16px 16px;
`

const Title = styled.div`
  font-size: var(--t-typography-font-size-xs);
  line-height: var(--t-typography-line-height-xs);
  font-weight: var(--t-typography-font-weight-medium);
  color: var(--sub-text-color);
  margin-bottom: 8px;
`

const ContentRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  /* Stretch so every metric card matches the tallest sibling (chips / lift %). */
  align-items: stretch;

  @media (max-width: 1200px) {
    flex-wrap: wrap;
  }
`

const MetricCard = styled.div`
  flex: 1 0 0;
  min-width: 220px;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: var(--t-borders-radius-rounded-lg);
  border: 1px solid var(--psima-metric-card-border);
  background: var(--psima-metric-card-bg);
  padding: 12px 14px;
  overflow: hidden;
`

const MetricContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  flex: 1;
  gap: 2px;
  /*
   * Always reserve the second text/chip row so cards without chips stay the same
   * height as cards that have them. 18px matches CompactBadge; xs sublabel fits
   * inside that slot.
   */
  min-height: calc(var(--t-typography-line-height-2xl) + 2px + 18px);
`

/* Value and unit share one baseline-aligned row. That is what keeps each card to
   two text rows, so adding the chips costs no height and the bar stays at the
   same 130px the Spacer above reserves. */
const ValueRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
`

const MetricValue = styled.div`
  font-size: 22px;
  line-height: var(--t-typography-line-height-2xl);
  font-weight: var(--t-typography-font-weight-semi-bold);
  color: var(--psima-metric-value-color);
  white-space: nowrap;
`

/* The denominator is context, not the headline. Same colour so it still reads as
   part of one number, but stepped down in size and weight so the count the user
   is actually changing wins the glance. */
const MetricTotal = styled.span`
  font-size: var(--t-typography-font-size-sm);
  line-height: var(--t-typography-line-height-sm);
  font-weight: var(--t-typography-font-weight-medium);
`

const MetricUnit = styled.div`
  font-size: var(--t-typography-font-size-sm);
  line-height: var(--t-typography-line-height-sm);
  font-weight: var(--t-typography-font-weight-medium);
  color: var(--color-text);
  white-space: normal;
  overflow-wrap: anywhere;
`

const IconCircle = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--t-borders-radius-rounded-full);
  background: var(--psima-metric-icon-bg);
  display: flex;
  align-items: center;
  justify-content: center;
`

const MetricSubLabel = styled.div`
  font-size: var(--t-typography-font-size-xs);
  line-height: var(--t-typography-line-height-xs);
  color: var(--sub-text-color);
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Chips = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
`

/*
 * Chips are stepped down below the DS Badge `small` size (20px tall, 12px text).
 * At the DS size they competed with the metric values for first glance, and the
 * numbers have to win that — the chips are the third thing to read in each card,
 * after the value and the label.
 *
 * NOTE: this is a deliberate deviation. If the pattern holds elsewhere, the right
 * fix is an `xs` size on the DS Badge rather than this local override.
 */
const CompactBadge = styled(Badge)`
  && {
    height: 18px;
    padding: 0 var(--t-spacing-1-5);
    font-size: 11px;
    gap: var(--t-spacing-1);
  }
`

/* The DS Badge status dot is 8px at size=small, which overpowers the chip text at
   this size, so the dot is supplied as leftElement at 5px instead. */
const RiskDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: var(--t-borders-radius-rounded-full);
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

/*
 * Behavioural chip: colour + leading icon both come from BEHAVIORAL_CHIP, so this
 * matches the signal chip rendered beside the employee's name in the participants
 * table. fill="currentColor" picks up the Badge's own text colour, the same way
 * the table's RiskSignalChip does it.
 */
const BehavioralChip = ({ signal, dataTestId, children }) => {
  const { bg, text, Icon } = BEHAVIORAL_CHIP[signal]

  return (
    <CompactBadge
      dataTestId={dataTestId}
      size={BADGE_SIZES.SMALL}
      shape={SHAPES.SQUARE}
      color={bg}
      textColor={text}
      leftElement={<Icon width="12px" height="12px" fill="currentColor" />}
    >
      {children}
    </CompactBadge>
  )
}

const ActionColumn = styled.div`
  flex: 1 0 0;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
`

const ScopeText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 26px;
  font-size: var(--t-typography-font-size-xs);
  line-height: var(--t-typography-line-height-xs);
  color: var(--sub-text-color);
`

const ScopeDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: var(--t-borders-radius-rounded-full);
  background: var(--sub-text-color);
`

const VerifiedTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: default;
`

// V3 SAFE uses var(--white) for label color; in dark mode --white is a dark gray.
// PSIMA quick launch uses dedicated tokens (green-600 fill — active but not neon).
const QuickLaunchButton = styled(Button)`
  width: 100%;
  && > span {
    width: 100%;
    height: 43px;
    color: var(--psima-quick-launch-button-text);
    background-color: var(--psima-quick-launch-button-bg);
    border-color: transparent;
  }

  &&:not(:disabled):hover > span,
  &&:not(:disabled):focus > span,
  &&:not(:disabled):focus-visible > span,
  &&:not(:disabled):focus-within > span,
  &&:not(:disabled):active > span {
    color: var(--psima-quick-launch-button-text);
    background-color: var(--psima-quick-launch-button-bg-hover);
    border-color: transparent;
  }

  &&:not(:disabled):focus-visible > span {
    box-shadow: 0 0 0 3px var(--psima-quick-launch-button-focus-ring);
  }

  &&:disabled > span {
    color: var(--psima-quick-launch-button-text);
    background-color: var(--psima-quick-launch-button-bg);
  }

  && > span > span {
    font-weight: var(--t-typography-font-weight-semi-bold);
  }
`

const ICON_FILL = 'var(--t-colors-blue-600)'
const ICON_SIZE = '19.2px'

const ImpactPreview = ({ setIsQuickLaunchModalOpen }) => {
  const dispatch = useDispatch()
  const metricBannerData = useSelector(selectMetricBannerData)
  const campaignsData = useSelector(selectCampaignsData)
  const excludedParticipants = useSelector(selectExcludedParticipants)

  const selectedCampaignsCount = useMemo(
    () =>
      campaignsData.selectedCampaigns.filter((campaignId) =>
        campaignsData.items.find(({ id }) => id === campaignId)
      ).length,
    [campaignsData.items, campaignsData.selectedCampaigns]
  )

  /*
   * The campaigns actually being launched: selected, and still holding at least
   * one non-excluded participant. Both the "risks addressed" numerator and its
   * per-level chip breakdown derive from this list, so the chips always sum to
   * the numerator.
   */
  const activeSelectedCampaigns = useMemo(() => {
    const activeIds = getActiveSelectedCampaignIds(
      campaignsData.items,
      campaignsData.selectedCampaigns,
      excludedParticipants
    )

    return activeIds
      .map((campaignId) => campaignsData.items.find(({ id }) => id === campaignId))
      .filter(Boolean)
  }, [campaignsData.items, campaignsData.selectedCampaigns, excludedParticipants])

  const activeCampaignsCount = activeSelectedCampaigns.length

  const includeNoParticipantsCount = useMemo(
    () =>
      campaignsData.selectedCampaigns.reduce((acc, id) => {
        const campaign = campaignsData.items.find(({ id: campaignId }) => campaignId === id)

        if (!campaign) {
          return acc
        }

        return (
          acc + campaign.audience.suggestedProfiles.length - (excludedParticipants[id]?.length || 0)
        )
      }, 0),
    [campaignsData.items, campaignsData.selectedCampaigns, excludedParticipants]
  )

  const { checkedCardsByParticipant, cardCountByParticipant } = useMemo(() => {
    const checkedCardsByParticipantMap = {}
    const cardCountByParticipantMap = {}

    campaignsData.items.forEach((item) => {
      if (!campaignsData.selectedCampaigns.includes(item.id)) return

      item.audience.suggestedProfiles.forEach(({ id }) => {
        cardCountByParticipantMap[id] = (cardCountByParticipantMap[id] || 0) + 1

        if (!excludedParticipants[item.id]?.includes(id)) {
          checkedCardsByParticipantMap[id] = [...(checkedCardsByParticipantMap[id] || []), item.id]
        }
      })
    })

    return {
      checkedCardsByParticipant: checkedCardsByParticipantMap,
      cardCountByParticipant: cardCountByParticipantMap,
    }
  }, [campaignsData.items, campaignsData.selectedCampaigns, excludedParticipants])

  const overlapCount = useMemo(() => {
    let count = 0

    campaignsData.selectedCampaigns.forEach((campaignId) => {
      const campaign = campaignsData.items.find(({ id }) => id === campaignId)

      if (!campaign) return

      campaign.audience.suggestedProfiles.forEach(({ id: participantId }) => {
        if ((cardCountByParticipant[participantId] || 0) < 2) return

        const checkedCards = checkedCardsByParticipant[participantId] || []
        if (checkedCards.length === 0) return
        if (
          !checkedCards.includes(campaignId) &&
          excludedParticipants[campaignId]?.includes(participantId)
        ) {
          count += 1
        }
      })
    })

    return count
  }, [
    campaignsData.items,
    campaignsData.selectedCampaigns,
    excludedParticipants,
    cardCountByParticipant,
    checkedCardsByParticipant,
  ])

  const companyRiskSummary = metricBannerData.data?.companyRiskSummary

  const selectedUniqueProfiles = useMemo(() => {
    const profilesById = new Map()

    campaignsData.selectedCampaigns.forEach((campaignId) => {
      const campaign = campaignsData.items.find(({ id }) => id === campaignId)

      if (!campaign) {
        return
      }

      const excludedIds = excludedParticipants[campaignId] || []

      campaign.audience.suggestedProfiles.forEach((profile) => {
        if (excludedIds.includes(profile.id)) {
          return
        }

        if (!profilesById.has(profile.id)) {
          profilesById.set(profile.id, profile)
        }
      })
    })

    return Array.from(profilesById.values())
  }, [campaignsData.items, campaignsData.selectedCampaigns, excludedParticipants])

  // X = unique uncovered users in selection (de-duped, after exclusions). Absolute
  // count only. isUncovered is snapshot-backed (last 90D, same as Top Banner).
  const uncoveredInSelectionCount = useMemo(
    () => selectedUniqueProfiles.filter((profile) => profile.isUncovered === true).length,
    [selectedUniqueProfiles]
  )

  /*
   * External Exposure — company-level OSINT risks (same Y / 7D window / taxonomy
   * as Top Banner). X is the count of active selected cards (1 card = 1 risk);
   * a/b/c split that same set by campaign.level.
   */
  const externalRisksTotal = useMemo(() => {
    const externalExposureByLevel = metricBannerData.data?.cardsExternalExposure?.data || {}

    return RISK_LEVEL_ORDER.reduce(
      (acc, level) => acc + Number(externalExposureByLevel[level]?.total || 0),
      0
    )
  }, [metricBannerData.data])

  const addressedRisksByLevel = useMemo(
    () =>
      activeSelectedCampaigns.reduce((acc, campaign) => {
        if (!campaign.level) {
          return acc
        }

        return { ...acc, [campaign.level]: (acc[campaign.level] || 0) + 1 }
      }, {}),
    [activeSelectedCampaigns]
  )

  /*
   * Behavioral Remediation (PRD §16, unchanged formula):
   * X = unique serial clickers (≥3) in selection who are also untrained.
   * Y = total serial clickers (≥3) + untrained in the org (Top Banner cohorts;
   *     no org-level AND count is exposed on metrics-banner).
   *
   * Chips (designer): mutually exclusive breakdown of high-behavioral-risk
   * profiles in the selection — serial clicker wins when both flags are set.
   */
  const serialClickersAddressedCount = useMemo(
    () =>
      selectedUniqueProfiles.filter(
        (profile) => profile.isSerialClicker === true && profile.isUntrained === true
      ).length,
    [selectedUniqueProfiles]
  )

  const totalSerialClickersAndUntrainedCount = useMemo(() => {
    const serialClickerCount = Number(companyRiskSummary?.behavioralRisk?.serialClickerCount || 0)
    const untrainedCount = Number(companyRiskSummary?.behavioralRisk?.untrainedCount || 0)

    return serialClickerCount + untrainedCount
  }, [companyRiskSummary])

  const selectedHighBehavioralRiskProfiles = useMemo(
    () => selectedUniqueProfiles.filter((profile) => profile.isHighBehavioralRisk === true),
    [selectedUniqueProfiles]
  )

  const selectedBehavioralSplit = useMemo(
    () => ({
      serialClickers: selectedHighBehavioralRiskProfiles.filter(
        (profile) => profile.isSerialClicker === true
      ).length,
      untrained: selectedHighBehavioralRiskProfiles.filter(
        (profile) => profile.isUntrained === true && profile.isSerialClicker !== true
      ).length,
    }),
    [selectedHighBehavioralRiskProfiles]
  )

  const isQuickLaunchDisabled =
    !campaignsData.selectedCampaigns.length || !includeNoParticipantsCount

  if (!selectedCampaignsCount) {
    return null
  }

  return (
    <>
      <Spacer />
      <StickyContainer data-testid="Psima_Dashboard_ImpactPreview_Container_div">
        <Title data-testid="Psima_Dashboard_ImpactPreview_Title_div">Impact Preview</Title>
        <ContentRow data-testid="Psima_Dashboard_ImpactPreview_ContentRow_div">
          <MetricCard data-testid="Psima_Dashboard_ImpactPreview_AwarenessCard_div">
            <IconCircle>
              <ShieldOutlineIcon width={ICON_SIZE} height={ICON_SIZE} fill={ICON_FILL} />
            </IconCircle>
            <MetricContent>
              <ValueRow>
                <MetricValue data-testid="Psima_Dashboard_ImpactPreview_AwarenessValue_div">
                  +<AnimatedNumber value={uncoveredInSelectionCount} />
                </MetricValue>
                <MetricUnit data-testid="Psima_Dashboard_ImpactPreview_AwarenessUnit_div">
                  employees covered
                </MetricUnit>
              </ValueRow>
              <MetricSubLabel data-testid="Psima_Dashboard_ImpactPreview_AwarenessSubLabel_div">
                Unique uncovered users in selection
              </MetricSubLabel>
            </MetricContent>
          </MetricCard>
          <MetricCard data-testid="Psima_Dashboard_ImpactPreview_ExposureCard_div">
            <IconCircle>
              <HexAlertIcon width={ICON_SIZE} height={ICON_SIZE} fill={ICON_FILL} />
            </IconCircle>
            <MetricContent>
              <ValueRow>
                <MetricUnit data-testid="Psima_Dashboard_ImpactPreview_ExposurePrefix_div">
                  Addresses
                </MetricUnit>
                <MetricValue data-testid="Psima_Dashboard_ImpactPreview_ExposureValue_div">
                  <CountOfTotal count={activeCampaignsCount} total={externalRisksTotal} />
                </MetricValue>
                <MetricUnit data-testid="Psima_Dashboard_ImpactPreview_ExposureUnit_div">
                  external risks
                </MetricUnit>
              </ValueRow>
              <Chips data-testid="Psima_Dashboard_ImpactPreview_ExposureChips_div">
                {RISK_LEVEL_ORDER.filter((level) => addressedRisksByLevel[level] > 0).map(
                  (level) => (
                    <CompactBadge
                      key={level}
                      dataTestId={`Psima_Dashboard_ImpactPreview_ExposureChip_${level}_div`}
                      size={BADGE_SIZES.SMALL}
                      shape={SHAPES.SQUARE}
                      color={RISK_CHIP[level].bg}
                      textColor={RISK_CHIP[level].text}
                      leftElement={<RiskDot $color={RISK_CHIP[level].text} />}
                    >
                      {`${addressedRisksByLevel[level]} ${RISK_LEVELS_LABELS[level]}`}
                    </CompactBadge>
                  )
                )}
              </Chips>
            </MetricContent>
          </MetricCard>
          <MetricCard data-testid="Psima_Dashboard_ImpactPreview_BehavioralCard_div">
            <IconCircle>
              <SerialClicker width={ICON_SIZE} height={ICON_SIZE} fill={ICON_FILL} />
            </IconCircle>
            <MetricContent>
              <ValueRow>
                <MetricValue data-testid="Psima_Dashboard_ImpactPreview_BehavioralValue_div">
                  <CountOfTotal
                    count={serialClickersAddressedCount}
                    total={totalSerialClickersAndUntrainedCount}
                  />
                </MetricValue>
                <MetricUnit data-testid="Psima_Dashboard_ImpactPreview_BehavioralUnit_div">
                  serial clickers addressed
                </MetricUnit>
              </ValueRow>
              <Chips data-testid="Psima_Dashboard_ImpactPreview_BehavioralChips_div">
                {selectedBehavioralSplit.serialClickers > 0 && (
                  <BehavioralChip
                    signal="serialClicker"
                    dataTestId="Psima_Dashboard_ImpactPreview_BehavioralChip_SerialClickers_div"
                  >
                    {`${selectedBehavioralSplit.serialClickers} serial clickers`}
                  </BehavioralChip>
                )}
                {selectedBehavioralSplit.untrained > 0 && (
                  <BehavioralChip
                    signal="untrained"
                    dataTestId="Psima_Dashboard_ImpactPreview_BehavioralChip_Untrained_div"
                  >
                    {`${selectedBehavioralSplit.untrained} untrained`}
                  </BehavioralChip>
                )}
              </Chips>
            </MetricContent>
          </MetricCard>
          <ActionColumn data-testid="Psima_Dashboard_ImpactPreview_ActionColumn_div">
            <ScopeText data-testid="Psima_Dashboard_ImpactPreview_ScopeText_div">
              <span data-testid="Psima_Dashboard_ImpactPreview_SelectedSimulations_div">
                {activeCampaignsCount} {activeCampaignsCount === 1 ? 'simulation' : 'simulations'}
              </span>
              <ScopeDot />
              <span data-testid="Psima_Dashboard_ImpactPreview_EmployeesInScope_div">
                {includeNoParticipantsCount}{' '}
                {includeNoParticipantsCount === 1 ? 'employee' : 'employees'}
              </span>
              {overlapCount > 0 && (
                <Tooltip
                  id="Psima_Dashboard_ImpactPreview_VerifiedByAgent_tooltip"
                  side="top"
                  arrow
                  content={
                    <>
                      <div style={{ fontWeight: 'var(--t-typography-font-weight-semi-bold)' }}>
                        Verified by the Agent ✅
                      </div>
                      <div>
                        Overlapping employees were assigned to their single most relevant
                        simulation, so no one is targeted twice in this launch.
                      </div>
                    </>
                  }
                >
                  <VerifiedTrigger data-testid="Psima_Dashboard_ImpactPreview_VerifiedByAgent_div">
                    <AgentVerifiedMark variant="banner" />
                  </VerifiedTrigger>
                </Tooltip>
              )}
            </ScopeText>
            <QuickLaunchButton
              id="Psima_Dashboard_ImpactPreview_QuickLaunch_Button"
              dataPendoFeature={PSIMA_PENDO_FEATURES.DASHBOARD_QUICK_LAUNCH}
              onClick={() => {
                dispatch({
                  type: SET_SINGLE_CAMPAIGN_ID,
                  payload: {
                    value: null,
                  },
                })

                setIsQuickLaunchModalOpen(true)
              }}
              disabled={isQuickLaunchDisabled}
              size={SIZES.XS}
              variant={VARIANTS.SAFE}
              dataTestId="Psima_Dashboard_ImpactPreview_QuickLaunch_Button"
            >
              {`Launch ${activeCampaignsCount} ${
                activeCampaignsCount === 1 ? 'simulation' : 'simulations'
              }`}
            </QuickLaunchButton>
          </ActionColumn>
        </ContentRow>
      </StickyContainer>
    </>
  )
}

export default ImpactPreview
